/*
 * hhs-port.js
 * Dependency-free replacement for Replo's runtime, covering the three
 * interactive component types our "ported" HHS sections keep verbatim:
 *
 *   1. Collapsible  - [data-replo-collapsible] with trigger/content parts
 *   2. Tabs         - [data-replo-component-root="tabs"] / [data-replo-tabs-trigger]
 *   3. Carousel     - [data-replo-carousel="true"] wrapper with slide-track,
 *                     nav-buttons (data-direction) and dot indicators
 *
 * Attribute names were taken verbatim from the rendered Replo markup, e.g.
 * sections/replo-hhs-haircare-section.liquid:
 *   <div data-replo-carousel="true" role="region" aria-label="Carousel">
 *   <div data-replo-component-root="carousel" data-replo-part="slide-track"
 *        style="transform:translate3d(0px,0px,0px);display:flex;...">
 *   <div data-replo-component-root="carousel" data-replo-part="slide" ...>
 *   <button data-replo-component-root="carousel" data-replo-part="nav-button"
 *           data-direction="previous|next">
 *   dots: <div role="button" aria-current="true|false" aria-label="Go to slide N">
 *
 * The runtime uses a single delegated click listener on `document`, plus an
 * init pass on DOMContentLoaded for initial state + carousel layout. It is
 * fully idempotent so the one-<script>-per-section loading pattern is safe.
 */
(function () {
  'use strict';

  // Idempotency guard: many sections each emit a <script src="hhs-port.js">,
  // so only the first one wires anything up.
  if (window.__hhsPortInit) return;
  window.__hhsPortInit = true;

  // ---------------------------------------------------------------------------
  // Small helpers
  // ---------------------------------------------------------------------------

  /** Run fn, swallowing any error so one broken component never breaks others. */
  function safe(fn) {
    try {
      return fn();
    } catch (e) {
      /* noop - defensive by design */
      return undefined;
    }
  }

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function qsa(root, selector) {
    if (!root || !root.querySelectorAll) return [];
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  // ---------------------------------------------------------------------------
  // 1. COLLAPSIBLE
  //
  // Replo markup (Radix-derived): a [data-replo-collapsible] root wraps a
  // trigger and a content region. The trigger carries data-radix-collapsible-
  // trigger / aria-expanded; the content carries data-state="open|closed".
  // Selectors are written broadly so we match either the part-id naming or the
  // radix naming, whichever a given ported section uses.
  // ---------------------------------------------------------------------------

  // Replo's ported accordion: a root with [data-state] (FAQ also keeps
  // [data-replo-collapsible]) wraps a trigger `<div role="button" aria-expanded
  // aria-controls>` whose FIRST child is the header (title + +/- icon) and LAST
  // child is the answer block. Replo's runtime swapped classes to show/hide the
  // answer; that CSS is gone, so we toggle the answer's display directly.
  var COLLAPSIBLE_TRIGGER = '[role="button"][aria-expanded][aria-controls]';
  var COLLAPSIBLE_ROOT = '[data-replo-collapsible],[data-state]';

  // Bootstrap-icons plus/minus paths Replo bakes into the +/- icon SVG. Replo's
  // runtime swapped the path on toggle; that's gone, so we swap it ourselves.
  var ICON_PLUS = 'M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4';
  var ICON_MINUS = 'M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8';

  function collapsibleAnswer(trigger) {
    // Answer = the trigger's last element child (only if there's more than the
    // header row). Guards against single-child triggers.
    if (!trigger || trigger.children.length < 2) return null;
    return trigger.children[trigger.children.length - 1];
  }

  function swapCollapsibleIcon(trigger, open) {
    var path = trigger.querySelector('svg[aria-label="Collapse/Expand"] path');
    if (path) path.setAttribute('d', open ? ICON_MINUS : ICON_PLUS);
  }

  function setCollapsible(trigger, open) {
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    // Find the collapsible ROOT (an ancestor). Search from the parent so we never
    // match the trigger itself — otherwise the root's data-state stays stale and
    // the open-state styling never applies to items opened after page load.
    var root = trigger.parentElement
      ? closest(trigger.parentElement, COLLAPSIBLE_ROOT)
      : null;
    if (root) root.setAttribute('data-state', open ? 'open' : 'closed');
    var answer = collapsibleAnswer(trigger);
    // Inline !important overrides Replo's class-based show/hide rules.
    if (answer) answer.style.setProperty('display', open ? 'block' : 'none', 'important');
    swapCollapsibleIcon(trigger, open);
  }

  function toggleCollapsible(trigger) {
    safe(function () {
      setCollapsible(trigger, trigger.getAttribute('aria-expanded') !== 'true');
    });
  }

  function initCollapsibles() {
    qsa(document, COLLAPSIBLE_TRIGGER).forEach(function (trigger) {
      safe(function () {
        setCollapsible(trigger, trigger.getAttribute('aria-expanded') === 'true');
      });
    });
  }

  // ---------------------------------------------------------------------------
  // 2. TABS
  //
  // Replo tabs: a tabs root ([data-replo-component-root="tabs"]) contains tab
  // triggers ([data-replo-tabs-trigger]) and panels. Triggers carry an index
  // (data-tab-id / data-replo-tab-index / aria-controls) that maps to a panel.
  // We match siblings within the same tabs root and toggle an active state.
  // ---------------------------------------------------------------------------

  var TABS_ROOT =
    '[data-replo-component-root="tabs"],[data-replo-tabs],[data-replo-tabs-list]';
  var TAB_TRIGGER = '[data-replo-tabs-trigger],[role="tab"]';
  var TAB_PANEL =
    '[data-replo-tabs-panel],[data-replo-part="tabs-panel"],[role="tabpanel"]';

  function tabKey(el) {
    if (!el) return null;
    return (
      el.getAttribute('data-tab-id') ||
      el.getAttribute('data-replo-tab-index') ||
      el.getAttribute('data-replo-tabs-trigger') ||
      el.getAttribute('aria-controls') ||
      null
    );
  }

  function panelKey(el) {
    if (!el) return null;
    return (
      el.getAttribute('data-tab-id') ||
      el.getAttribute('data-replo-tab-index') ||
      el.getAttribute('data-replo-tabs-panel') ||
      el.id ||
      null
    );
  }

  function activateTab(root, trigger) {
    safe(function () {
      if (!root || !trigger) return;
      var triggers = qsa(root, TAB_TRIGGER);
      var panels = qsa(root, TAB_PANEL);
      var key = tabKey(trigger);
      var activeIndex = triggers.indexOf(trigger);

      triggers.forEach(function (t, i) {
        var on = t === trigger;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('data-state', on ? 'active' : 'inactive');
        t.classList.toggle('is-active', on);
      });

      panels.forEach(function (p, i) {
        // Match by key when available, else fall back to positional index.
        var on = key != null ? panelKey(p) === key : i === activeIndex;
        p.setAttribute('data-state', on ? 'active' : 'inactive');
        p.classList.toggle('is-active', on);
        p.hidden = !on;
        p.style.display = on ? '' : 'none';
      });
    });
  }

  function initTabs() {
    qsa(document, TABS_ROOT).forEach(function (root) {
      safe(function () {
        var triggers = qsa(root, TAB_TRIGGER);
        if (!triggers.length) return;
        // Honor a markup-declared active tab, else default to the first.
        var active =
          triggers.filter(function (t) {
            return (
              t.getAttribute('aria-selected') === 'true' ||
              t.getAttribute('data-state') === 'active' ||
              t.classList.contains('is-active')
            );
          })[0] || triggers[0];
        activateTab(root, active);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // 3. CAROUSEL
  //
  // Scope = the [data-replo-carousel="true"] wrapper, which contains:
  //   - slide-track: [data-replo-part="slide-track"] (transform:translate3d)
  //   - slides:      [data-replo-part="slide"] (some are clones / aria-hidden)
  //   - nav-buttons: [data-replo-part="nav-button"] data-direction=previous|next
  //   - dots:        [role="button"][aria-current][aria-label^="Go to slide"]
  // We translate the track by slide width and clamp at the ends.
  // ---------------------------------------------------------------------------

  var CAROUSEL_ROOT = '[data-replo-carousel="true"]';
  var CAROUSEL_TRACK = '[data-replo-part="slide-track"]';
  var CAROUSEL_SLIDE = '[data-replo-part="slide"]';
  var CAROUSEL_NAV = '[data-replo-part="nav-button"]';
  var CAROUSEL_DOT = '[role="button"][aria-current]';

  // Per-carousel current index lives on the wrapper dataset.
  function getIndex(root) {
    var n = parseInt(root.getAttribute('data-hhs-index'), 10);
    return isNaN(n) ? 0 : n;
  }
  function setIndex(root, n) {
    root.setAttribute('data-hhs-index', String(n));
  }

  var CAROUSEL_TRANS = 'transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)';

  // Original slides = the ones we didn't clone. Replo's export marks EVERY real
  // slide data-is-cloned="true" (so that attribute is useless for us); instead we
  // tag the clones WE add with data-hhs-clone and exclude those.
  function originalSlides(track) {
    return qsa(track, CAROUSEL_SLIDE).filter(function (s) {
      return s.getAttribute('data-hhs-clone') !== '1';
    });
  }

  function slideStep(track, slides) {
    if (!slides.length) return 0;
    var first = slides[0];
    var width = first.getBoundingClientRect().width || first.offsetWidth || 0;
    // Include the flex gap if present (Replo's --replo-gap is usually unset → 0).
    var gap = 0;
    safe(function () {
      var cs = window.getComputedStyle(track);
      gap = parseFloat(cs.columnGap || cs.gap) || 0;
    });
    if (width < 1) width = 300; // defensive fallback if measured before layout
    return width + gap;
  }

  // How many whole slides fit in the carousel viewport (the track's parent).
  function visibleCount(root, track, step) {
    if (step <= 0) return 1;
    var vp = (track.parentElement && track.parentElement.getBoundingClientRect().width) ||
      root.getBoundingClientRect().width || step;
    return Math.max(1, Math.floor((vp + 1) / step));
  }

  function maxIndexFor(root, track, slides, step) {
    return Math.max(0, slides.length - visibleCount(root, track, step));
  }

  // Replo marks the active dot with an extra modifier CLASS (its runtime toggled
  // it); aria-current alone has no visual effect because the active styling lives
  // on that class. Discover it once per carousel by diffing the baked active dot
  // against an inactive one, and cache it on the wrapper dataset.
  function dotActiveClasses(root) {
    var cached = root.getAttribute('data-hhs-dotcls');
    if (cached !== null) return cached ? cached.split(' ') : [];
    var dots = qsa(root, CAROUSEL_DOT);
    var active = null, inactive = null;
    dots.forEach(function (d) {
      if (!active && d.getAttribute('aria-current') === 'true') active = d;
      else if (!inactive) inactive = d;
    });
    var extra = [];
    if (active && inactive) {
      var have = {};
      for (var i = 0; i < inactive.classList.length; i++) have[inactive.classList[i]] = 1;
      for (var j = 0; j < active.classList.length; j++) {
        if (!have[active.classList[j]]) extra.push(active.classList[j]);
      }
    }
    root.setAttribute('data-hhs-dotcls', extra.join(' '));
    return extra;
  }

  function normalizeExportedSlides(track) {
    originalSlides(track).forEach(function (slide) {
      // Replo exports these slides with data-is-cloned="true" and hides every
      // non-active slide inline (opacity:0 / z-index:-1). Our native runtime
      // treats exported slides as originals, so normalize them before measuring
      // or translating the track; otherwise slide 2+ can render as a blank pane.
      slide.style.display = 'flex';
      slide.style.opacity = '1';
      slide.style.zIndex = 'auto';
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('inert');
      // NOTE: slide images deliberately stay loading="lazy". Blanket-eager here
      // used to pull every slide of every carousel (plus clones) into the
      // initial page load — multiple MB before any scroll. primeSlides() below
      // fetches just-in-time instead.
    });
  }

  // Force-fetch the images of the slide at physical track position `centerPos`
  // and its immediate neighbors (covers loop clones, whose physical position is
  // logical index + k). Flipping loading to eager starts the fetch immediately.
  function primeSlides(track, centerPos) {
    var all = qsa(track, CAROUSEL_SLIDE);
    for (var off = -1; off <= 1; off++) {
      var slide = all[centerPos + off];
      if (!slide) continue;
      qsa(slide, 'img').forEach(function (img) {
        if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      });
    }
  }

  // When a carousel approaches the viewport, prime its current slide and
  // neighbors so the first paint and the first swipe are both flash-free,
  // without paying for any of it at initial page load.
  var primeObserver = null;
  function observeForPriming(root) {
    if (root.getAttribute('data-hhs-prime') === '1') return;
    root.setAttribute('data-hhs-prime', '1');
    if (!window.IntersectionObserver) {
      var tr = root.querySelector(CAROUSEL_TRACK);
      if (tr) primeSlides(tr, getIndex(root) + loopK(root));
      return;
    }
    if (!primeObserver) {
      primeObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var r = entry.target;
          primeObserver.unobserve(r);
          safe(function () {
            var track = r.querySelector(CAROUSEL_TRACK);
            if (track) primeSlides(track, getIndex(r) + loopK(r));
          });
        });
      }, { rootMargin: '600px 0px' });
    }
    primeObserver.observe(root);
  }

  // Replo's runtime normally turns the slides wrapper into the clip window and
  // animates the track. With the runtime gone we set that up once ourselves, then
  // build the seamless loop.
  function setupCarousel(root) {
    safe(function () {
      var track = root.querySelector(CAROUSEL_TRACK);
      if (!track) return;
      var viewport = track.parentElement;
      if (viewport && viewport.getAttribute('data-hhs-vp') !== '1') {
        viewport.style.overflowX = 'hidden';
        viewport.setAttribute('data-hhs-vp', '1');
      }
      normalizeExportedSlides(track);
      track.style.transition = CAROUSEL_TRANS;
      track.style.willChange = 'transform';
      buildLoop(root, track);

      // Secondary tracks (product-offer thumbnail strip): never translated, so
      // thumbs past the viewport edge were unreachable. Native horizontal
      // scrolling gives touch swipe for free; scrollbar is hidden via the
      // injected .hhs-thumb-scroll rule.
      var tracks = qsa(root, CAROUSEL_TRACK);
      for (var t = 1; t < tracks.length; t++) {
        var tvp = tracks[t].parentElement;
        if (tvp && !tvp.classList.contains('hhs-thumb-scroll')) {
          tvp.classList.add('hhs-thumb-scroll');
        }
      }
    });
  }

  function markClone(el) {
    el.setAttribute('data-hhs-clone', '1');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('tabindex', '-1');
    return el;
  }

  function loopK(root) {
    return parseInt(root.getAttribute('data-hhs-loopk') || '0', 10);
  }

  // Clone a viewport's worth (k) of slides onto BOTH ends so scrolling past either
  // edge reveals the opposite end. Only when there are more slides than fit. Once.
  function buildLoop(root, track) {
    if (root.getAttribute('data-hhs-looped') === '1') return;
    root.setAttribute('data-hhs-looped', '1');
    var originals = originalSlides(track);
    var step = slideStep(track, originals);
    var vc = visibleCount(root, track, step);
    if (originals.length <= vc) { root.setAttribute('data-hhs-loopk', '0'); return; }
    var k = Math.min(vc, originals.length);
    var n = originals.length;
    // Head clones (first k) → append in order.
    for (var i = 0; i < k; i++) track.appendChild(markClone(originals[i].cloneNode(true)));
    // Tail clones (last k) → prepend so the lead-in reads ...n-2, n-1, | 0, 1...
    for (var j = 0; j < k; j++) {
      track.insertBefore(markClone(originals[n - 1 - j].cloneNode(true)), track.firstChild);
    }
    root.setAttribute('data-hhs-loopk', String(k));
  }

  function updateProductThumbs(root, logical, n) {
    // Product-offer gallery thumbnails are a second Replo slide track nested in
    // the same carousel wrapper. Replo's runtime used to apply the active thumb
    // treatment; the native runtime now mirrors the logical main-slide index.
    if (!root || root.id !== 'replo-carousel::Rbfr2:') return;
    var tracks = qsa(root, CAROUSEL_TRACK);
    if (tracks.length < 2 || !n) return;
    var active = ((logical % n) + n) % n;
    originalSlides(tracks[1]).forEach(function (slide, i) {
      var on = i === active;
      slide.classList.toggle('hhs-product-thumb-active', on);
      slide.setAttribute('aria-current', on ? 'true' : 'false');
      slide.style.cursor = 'pointer';
      qsa(slide, 'picture,img').forEach(function (el) {
        el.style.borderRadius = '8px';
        el.style.transition = 'border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease';
        el.style.border = on ? '2px solid #3b332f' : '1px solid rgba(59,51,47,0.18)';
        el.style.boxShadow = on ? '0 0 0 2px rgba(59,51,47,0.16)' : 'none';
        el.style.opacity = on ? '1' : '0.72';
      });
      if (on) {
        // Keep the active thumb in view inside the scrollable strip. Manual
        // scrollLeft (not scrollIntoView) so the page never scrolls vertically.
        var vp = tracks[1].parentElement;
        if (vp && vp.classList.contains('hhs-thumb-scroll')) {
          safe(function () {
            var vr = vp.getBoundingClientRect();
            var sr = slide.getBoundingClientRect();
            var target = vp.scrollLeft + (sr.left - vr.left) - (vr.width - sr.width) / 2;
            vp.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
          });
        }
      }
    });
  }

  function updateDots(root, logical, n) {
    var dots = qsa(root, CAROUSEL_DOT);
    if (!dots.length) return;
    var activeCls = dotActiveClasses(root);
    var active = ((logical % n) + n) % n;
    dots.forEach(function (dot, i) {
      var on = i === active;
      dot.setAttribute('aria-current', on ? 'true' : 'false');
      for (var c = 0; c < activeCls.length; c++) dot.classList.toggle(activeCls[c], on);
    });
  }

  function applyTransform(track, pos, step, animate) {
    track.style.transition = animate ? CAROUSEL_TRANS : 'none';
    track.style.transform = 'translate3d(' + (-(pos * step)) + 'px, 0px, 0px)';
    if (!animate) {
      void track.offsetWidth; // flush the jump before re-enabling animation
      track.style.transition = CAROUSEL_TRANS;
    }
  }

  // Position the track for the current index without animating (load / resize).
  function layoutCarousel(root) {
    safe(function () {
      var track = root.querySelector(CAROUSEL_TRACK);
      if (!track) return;
      var slides = originalSlides(track);
      if (!slides.length) return;
      var step = slideStep(track, slides);
      var k = loopK(root);
      var n = slides.length;

      var index;
      if (k > 0) {
        index = ((getIndex(root) % n) + n) % n;
      } else {
        index = Math.min(Math.max(0, getIndex(root)), maxIndexFor(root, track, slides, step));
      }
      setIndex(root, index);

      applyTransform(track, index + k, step, false);
      updateDots(root, index, n);
      updateProductThumbs(root, index, n);

      qsa(root, CAROUSEL_NAV).forEach(function (btn) {
        if (k > 0) { btn.disabled = false; btn.setAttribute('aria-disabled', 'false'); return; }
        var dir = btn.getAttribute('data-direction');
        var mi = maxIndexFor(root, track, slides, step);
        var atBound = (dir === 'previous' && index <= 0) || (dir === 'next' && index >= mi);
        btn.disabled = atBound;
        btn.setAttribute('aria-disabled', atBound ? 'true' : 'false');
      });
    });
  }

  // Animate to a logical index (which may be -1 or n, i.e. into the clones); when
  // the transition finishes, silently snap to the equivalent real slide.
  function slideTo(root, logical) {
    safe(function () {
      var track = root.querySelector(CAROUSEL_TRACK);
      if (!track || root.getAttribute('data-hhs-anim') === '1') return;
      var slides = originalSlides(track);
      var n = slides.length;
      if (!n) return;
      var step = slideStep(track, slides);
      var k = loopK(root);

      if (k === 0) {
        var clamped = Math.max(0, Math.min(logical, maxIndexFor(root, track, slides, step)));
        setIndex(root, clamped);
        primeSlides(track, clamped);
        applyTransform(track, clamped, step, true);
        updateDots(root, clamped, n);
        updateProductThumbs(root, clamped, n);
        return;
      }

      var real = ((logical % n) + n) % n;
      primeSlides(track, logical + k);
      primeSlides(track, real + k);
      applyTransform(track, logical + k, step, true);
      setIndex(root, real);
      updateDots(root, real, n);
      updateProductThumbs(root, real, n);

      if (real !== logical) {
        // Landed on a clone — snap to the real slide once the animation ends.
        root.setAttribute('data-hhs-anim', '1');
        var snap = function () {
          track.removeEventListener('transitionend', snap);
          applyTransform(track, real + k, step, false);
          root.setAttribute('data-hhs-anim', '0');
        };
        track.addEventListener('transitionend', snap);
        setTimeout(function () {
          if (root.getAttribute('data-hhs-anim') === '1') snap();
        }, 650);
      }
    });
  }

  function goToSlide(root, index) {
    slideTo(root, index);
  }

  function navCarousel(root, direction) {
    slideTo(root, getIndex(root) + (direction === 'next' ? 1 : -1));
  }

  // ---------------------------------------------------------------------------
  // Swipe / drag (pointer events) for every carousel's primary track.
  //
  // touch-action: pan-y leaves vertical page scrolling to the browser; once a
  // gesture is judged horizontal we drag the track 1:1 with the finger and on
  // release either advance one slide (past the distance threshold) or snap
  // back. A capture-phase click suppressor stops the click that follows a drag
  // from hitting links/thumbs/dots inside the carousel.
  // ---------------------------------------------------------------------------

  var suppressClicksUntil = 0;

  function attachSwipe(root) {
    if (root.getAttribute('data-hhs-swipe') === '1') return;
    root.setAttribute('data-hhs-swipe', '1');
    var track = root.querySelector(CAROUSEL_TRACK);
    if (!track || !window.PointerEvent) return;
    var viewport = track.parentElement || track;
    viewport.style.touchAction = 'pan-y';

    var pointerId = null;
    var startX = 0, startY = 0, dx = 0;
    var axis = null; // null = undecided, true = horizontal, false = vertical
    var step = 0, startIdx = 0, k = 0, maxIdx = 0;

    function endDrag(commit) {
      if (pointerId === null) return;
      safe(function () { viewport.releasePointerCapture(pointerId); });
      pointerId = null;
      if (axis !== true) return;
      axis = null;
      if (Math.abs(dx) > 10) suppressClicksUntil = Date.now() + 400;
      var threshold = Math.min(80, step * 0.25);
      if (commit && dx <= -threshold) slideTo(root, startIdx + 1);
      else if (commit && dx >= threshold) slideTo(root, startIdx - 1);
      else slideTo(root, startIdx); // snap back
    }

    viewport.addEventListener('pointerdown', function (e) {
      if (!e.isPrimary || root.getAttribute('data-hhs-anim') === '1') return;
      // Native image drag / text selection would hijack mouse gestures.
      if (e.pointerType === 'mouse') e.preventDefault();
      var slides = originalSlides(track);
      if (slides.length < 2) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      dx = 0;
      axis = null;
      step = slideStep(track, slides);
      k = loopK(root);
      startIdx = getIndex(root);
      maxIdx = maxIndexFor(root, track, slides, step);
    });

    viewport.addEventListener('pointermove', function (e) {
      if (e.pointerId !== pointerId) return;
      dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < 7 && Math.abs(dy) < 7) return;
        axis = Math.abs(dx) > Math.abs(dy);
        if (!axis) { pointerId = null; return; } // vertical: let the page scroll
        safe(function () { viewport.setPointerCapture(pointerId); });
      }
      if (axis === true && e.cancelable) e.preventDefault();
      if (axis !== true || step <= 0) return;
      var drag = dx;
      if (k === 0) {
        // Rubber-band beyond the ends instead of dragging into nothing.
        var over = 0;
        if (startIdx * step - drag < 0) over = drag - startIdx * step;
        else if (startIdx * step - drag > maxIdx * step) over = drag + (maxIdx - startIdx) * step;
        if (over !== 0) drag = drag - over + over * 0.3;
      }
      track.style.transition = 'none';
      track.style.transform =
        'translate3d(' + (-((startIdx + k) * step) + drag) + 'px, 0px, 0px)';
    });

    viewport.addEventListener('pointerup', function (e) {
      if (e.pointerId === pointerId) endDrag(true);
    });
    // pointercancel = the browser claimed the gesture (scroll takeover). The
    // touchmove preventDefault below makes this rare, but when it still fires
    // mid-drag, honor the distance already travelled instead of snapping back.
    viewport.addEventListener('pointercancel', function (e) {
      if (e.pointerId === pointerId) endDrag(true);
    });

    // touch-action: pan-y alone is not enough on iOS Safari: a slight vertical
    // drift in a horizontal swipe lets the browser start a page scroll and
    // pointercancel our drag ("swipe starts then freezes"). Once the axis lock
    // says horizontal, block the native scroll for the rest of the gesture.
    viewport.addEventListener(
      'touchmove',
      function (e) {
        if (axis === true && pointerId !== null && e.cancelable) e.preventDefault();
      },
      { passive: false }
    );
  }

  // Swallow the click that immediately follows a drag gesture.
  document.addEventListener(
    'click',
    function (e) {
      if (Date.now() < suppressClicksUntil) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  function initCarousels() {
    qsa(document, CAROUSEL_ROOT).forEach(function (root) {
      if (!root.hasAttribute('data-hhs-index')) setIndex(root, 0);
      setupCarousel(root);
      layoutCarousel(root);
      attachSwipe(root);
      observeForPriming(root);
    });
  }

  // ---------------------------------------------------------------------------
  // Delegated click handling (single listener for all sections / components)
  // ---------------------------------------------------------------------------

  document.addEventListener(
    'click',
    function (e) {
      var target = e.target;
      if (!target || !target.closest) return;

      // Carousel nav button
      var nav = closest(target, CAROUSEL_NAV);
      if (nav) {
        var croot = closest(nav, CAROUSEL_ROOT);
        if (croot) {
          navCarousel(croot, nav.getAttribute('data-direction'));
          return;
        }
      }

      // Thumbnail slide (product-offer gallery): the thumbs are a SECOND slide
      // track inside the same carousel root. Clicking one selects that image in
      // the main gallery. Slides of the primary track are deliberately ignored.
      var thumbSlide = closest(target, CAROUSEL_SLIDE);
      if (thumbSlide) {
        var thumbRoot = closest(thumbSlide, CAROUSEL_ROOT);
        var thumbTrack = closest(thumbSlide, CAROUSEL_TRACK);
        if (thumbRoot && thumbTrack && thumbTrack !== thumbRoot.querySelector(CAROUSEL_TRACK)) {
          var thumbIdx = originalSlides(thumbTrack).indexOf(thumbSlide);
          if (thumbIdx > -1) {
            goToSlide(thumbRoot, thumbIdx);
            return;
          }
        }
      }

      // Carousel dot
      var dot = closest(target, CAROUSEL_DOT);
      if (dot) {
        var dotRoot = closest(dot, CAROUSEL_ROOT);
        if (dotRoot) {
          var dots = qsa(dotRoot, CAROUSEL_DOT);
          var i = dots.indexOf(dot);
          if (i > -1) {
            goToSlide(dotRoot, i);
            return;
          }
        }
      }

      // Tab trigger
      var tab = closest(target, TAB_TRIGGER);
      if (tab) {
        var troot = closest(tab, TABS_ROOT);
        if (troot) {
          activateTab(troot, tab);
          return;
        }
      }

      // Collapsible trigger
      var trigger = closest(target, COLLAPSIBLE_TRIGGER);
      if (trigger && closest(trigger, COLLAPSIBLE_ROOT)) {
        toggleCollapsible(trigger);
        return;
      }
    },
    false
  );

  // ---------------------------------------------------------------------------
  // Init pass
  // ---------------------------------------------------------------------------

  // Replo's "Slide X of X" live-region is meant to be screen-reader-only, but
  // the reset that hid it was stripped. Re-add the sr-only rule so it's hidden
  // visually while staying accessible.
  function injectSrOnlyStyle() {
    if (document.getElementById('hhs-port-sronly')) return;
    var st = document.createElement('style');
    st.id = 'hhs-port-sronly';
    st.textContent =
      '.replo-sr-only{position:absolute!important;width:1px!important;height:1px!important;' +
      'padding:0!important;margin:-1px!important;overflow:hidden!important;' +
      'clip:rect(0 0 0 0)!important;white-space:nowrap!important;border:0!important}' +
      // Smooth-scroll CTA anchors (e.g. href="#hhs-offer") to the product form.
      'html{scroll-behavior:smooth}' +
      // Carousel arrows: most <button>s carry a 1px square border (radius 0) that
      // Replo's runtime hid — that's the stray rectangle. Strip the button border;
      // the circular ring belongs on the inner wrapper, so normalize that to a
      // circle (restores growth-unlock's ring, whose inner border is 0px).
      '[data-replo-part="nav-button"]{border:none!important;outline:none!important;box-shadow:none!important}' +
      '[data-replo-part="nav-button"]>*{border:2px solid currentColor!important;border-radius:9999px!important;box-sizing:border-box!important}' +
      // Thumbnail strip: natively scrollable (touch swipe for free), scrollbar hidden.
      '.hhs-thumb-scroll{overflow-x:auto!important;-webkit-overflow-scrolling:touch;scrollbar-width:none;-ms-overflow-style:none}' +
      '.hhs-thumb-scroll::-webkit-scrollbar{display:none}';
    (document.head || document.documentElement).appendChild(st);
  }

  function init() {
    safe(injectSrOnlyStyle);
    safe(initCollapsibles);
    safe(initTabs);
    safe(initCarousels);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-layout carousels on resize (slide width is viewport-dependent).
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      safe(initCarousels);
    }, 150);
  });
})();
